"use client"

import { useMutation, useQueryClient } from "@tanstack/react-query"
import { toast } from "sonner"
import {
  changeAiDisclosureSettingAction,
  saveAiDisclosureTemplateAction,
  syncVoiceDisclosureOpeningsAction,
} from "@/server-actions/ai-disclosure-settings"
import { aiDisclosureSettingsQueryPrefix } from "@/hooks/queries/ai-disclosure-settings"
import { useTranslatedQueryHandling } from "@/hooks/use-translated-query-handling"

import type { AiDisclosureTemplateChannel } from "@/lib/ai-disclosure/settings"
import { useTranslations } from "next-intl"


export function useChangeAiDisclosureSettingMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsAiDisclosureSettings")

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { enabled: boolean; expectedEnabled: boolean; acceptedConfirmationText?: string }) => {
      const result = await withQueryHandling(changeAiDisclosureSettingAction(input), {
        loading: tUi("updatingAIDisclosure"),
        success: tUi("aIDisclosureUpdated"),
        showErrorToast: false,
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: aiDisclosureSettingsQueryPrefix })
      if (result.voiceSyncFailed > 0) {
        toast.warning(tUi("value0VoiceAgentsCouldNotBeUpdatedRetry", { value0: String(result.voiceSyncFailed) }))
      }
    },
  })
}

export function useSaveAiDisclosureTemplateMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsAiDisclosureSettings")

  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async (input: { channel: AiDisclosureTemplateChannel; language: string; template: string }) => {
      const result = await withQueryHandling(saveAiDisclosureTemplateAction(input), {
        loading: tUi("savingAIDisclosureWording"),
        success: tUi("aIDisclosureWordingSaved"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      return result
    },
    onSuccess: async (result) => {
      await queryClient.invalidateQueries({ queryKey: aiDisclosureSettingsQueryPrefix })
      if (result.voiceSyncFailed > 0) {
        toast.warning(tUi("value0VoiceAgentsCouldNotBeUpdatedRetry", { value0: String(result.voiceSyncFailed) }))
      }
    },
  })
}

export function useSyncVoiceDisclosureOpeningsMutation() {
  const withQueryHandling = useTranslatedQueryHandling()

  const tUi = useTranslations("Feedback.MutationsAiDisclosureSettings")

  return useMutation({
    mutationFn: async () => {
      const result = await withQueryHandling(syncVoiceDisclosureOpeningsAction({}), {
        loading: tUi("synchronizingVoiceOpenings"),
        success: tUi("voiceOpeningsSynchronized"),
      })
      if ("status" in result) throw new Error(result.errorMessage)
      if (result.failed > 0) {
        toast.warning(tUi("value0VoiceAgentsCouldNotBeUpdated", { value0: String(result.failed) }))
      }
      return result
    },
  })
}
